import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  CheckBox,
  Select,
  Tabs,
  TopProgress,
} from "../../../../components";
import { clientService, permissionsService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";
import useDebounce from "../../../../hooks/useDebounce";
import PermissionsSelector from "./PermissionsSelector";
import {
  ActionsRow,
  Card,
  EmptyState,
  InfoBox,
  InfoText,
  PermissionsWrapper,
  SectionTitle,
  SelectionRow,
  TabsWrapper,
} from "../styles";
import {
  flattenPermissions,
  GroupPermissions,
  PermissionsByCategory,
  SelectorOption,
} from "../types";

const GroupsTab: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();

  const [groupOptions, setGroupOptions] = useState<SelectorOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectorOption | null>(
    null
  );
  const [groupPermissions, setGroupPermissions] =
    useState<GroupPermissions | null>(null);
  const [activePanel, setActivePanel] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [hasGroups, setHasGroups] = useState<boolean>(true);
  const debouncedSearch = useDebounce(search, 300);

  const mapGroupToOption = useCallback(
    (group: any): SelectorOption => ({
      id: group.id,
      value: group.id,
      label: group.name || group.display || `#${group.id}`,
    }),
    []
  );

  const loadGroups = useCallback(
    async (
      searchValue: string,
      fallbackOption?: SelectorOption | null,
      updateAvailability?: boolean
    ) => {
      try {
        const response = await clientService.getUserGroups({
          page: 1,
          pageSize: 20,
          ordering: "name",
          search: searchValue || "",
        });
        const objects = response?.data?.objects || [];
        if (updateAvailability) {
          const total = response?.data?.count ?? objects.length;
          setHasGroups(Boolean(total));
        }
        const mapped = objects.map((group: any) => mapGroupToOption(group));
        if (
          fallbackOption &&
          !mapped.some((option: SelectorOption) => option.id === fallbackOption.id)
        ) {
          mapped.push(fallbackOption);
        }
        setGroupOptions(mapped);
      } catch (error) {
        console.error("Failed to load groups", error);
        if (updateAvailability) {
          setHasGroups(false);
        }
      }
    },
    [mapGroupToOption]
  );

  useEffect(() => {
    loadGroups(debouncedSearch, selectedOption, !search);
  }, [debouncedSearch, loadGroups, search, selectedOption]);

  useEffect(() => {
    if (!groupPermissions) return;
    const panelKeys = Object.keys(groupPermissions.permissions || {});
    if (panelKeys.length && !panelKeys.includes(activePanel)) {
      setActivePanel(panelKeys[0]);
    }
  }, [groupPermissions, activePanel]);

  const handleGroupChange = useCallback(
    async (option: SelectorOption | null) => {
      setSelectedOption(option);
      setGroupPermissions(null);
      setActivePanel("");

      if (!option?.id) return;

      setLoading(true);
      try {
        const permissionsRes = await permissionsService.getGroupPermissions(
          String(option.id)
        );
        const permissionsData = permissionsRes?.data as GroupPermissions;
        setGroupPermissions(permissionsData);
        const firstPanel = Object.keys(
          permissionsData?.permissions || {}
        )[0];
        if (firstPanel) {
          setActivePanel(firstPanel);
        }
      } catch (e: any) {
        viewAlert({
          severity: "error",
          message:
            e?.response?.data?.detail ||
            e?.response?.data?.error ||
            t("FAILED_TO_LOAD_DATA"),
        });
      } finally {
        setLoading(false);
      }
    },
    [t, viewAlert]
  );

  const panelTabs = useMemo(() => {
    if (!groupPermissions?.permissions) {
      return [];
    }
    return Object.keys(groupPermissions.permissions).map((panelKey: string) => ({
      title:
        groupPermissions.panel_display_names?.[panelKey] ||
        panelKey.replace(/_/g, " "),
      slug: panelKey,
      id: panelKey,
    }));
  }, [groupPermissions]);

  const handlePanelPermissionsChange = useCallback(
    (panelKey: string, updated: PermissionsByCategory) => {
      setGroupPermissions((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          permissions: {
            ...prev.permissions,
            [panelKey]: updated,
          },
        };
      });
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!groupPermissions) {
      viewAlert({
        severity: "error",
        message: t("PLEASE_SELECT_GROUP"),
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...groupPermissions,
        permissions: flattenPermissions(groupPermissions.permissions),
      };
      await permissionsService.saveGroupPermissions(payload);
      viewAlert({
        severity: "success",
        message: t("AUTHORIZATION_GROUP_PERMISSIONS_SAVED"),
      });
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          t("ERROR_OCCURRED"),
      });
    } finally {
      setSaving(false);
    }
  }, [groupPermissions, t, viewAlert]);

  if (!hasGroups) {
    return (
      <Card>
        <SectionTitle>{t("AUTHORIZATION_SELECT_GROUP_TITLE")}</SectionTitle>
        <EmptyState>{t("AUTHORIZATION_NO_GROUPS")}</EmptyState>
      </Card>
    );
  }

  const permissionsForPanel =
    activePanel && groupPermissions?.permissions?.[activePanel];

  return (
    <Card>
      <TopProgress loading={loading || saving} />
      <SectionTitle>{t("AUTHORIZATION_SELECT_GROUP_TITLE")}</SectionTitle>
      <SelectionRow>
        <Select
          data={groupOptions}
          selectedValue={selectedOption?.id || ""}
          onChange={(option: any) => handleGroupChange(option)}
          hasSearch
          onSearchChange={setSearch}
          title={t("AUTHORIZATION_GROUP")}
          placeholder={t("AUTHORIZATION_SELECT_GROUP_PLACEHOLDER")}
        />
      </SelectionRow>

      {groupPermissions && (
        <>
          <InfoBox>
            <InfoText>{t("AUTHORIZATION_GROUP_INFO")}</InfoText>
          </InfoBox>
          <CheckBox
            title={t("AUTHORIZATION_GRANT_IMPLICITLY")}
            value={groupPermissions.implicitly_granted}
            onChange={() =>
              setGroupPermissions((prev) =>
                prev
                  ? {
                      ...prev,
                      implicitly_granted: !prev.implicitly_granted,
                    }
                  : prev
              )
            }
          />
          <PermissionsWrapper>
            <TabsWrapper>
              <Tabs
                tabs={panelTabs}
                size="display"
                blockNavigate
                location={activePanel}
                onChange={(panel) => setActivePanel(panel)}
              />
            </TabsWrapper>
            {permissionsForPanel ? (
              <PermissionsSelector
                permissions={permissionsForPanel}
                onChange={(updated) =>
                  handlePanelPermissionsChange(activePanel, updated)
                }
              />
            ) : (
              <EmptyState>{t("AUTHORIZATION_NO_PERMISSIONS_FOUND")}</EmptyState>
            )}
          </PermissionsWrapper>
          <ActionsRow>
            <Button
              size="mid"
              title={t("AUTHORIZATION_SAVE_PERMISSIONS")}
              onClick={handleSave}
              disabled={saving || !groupPermissions}
            />
          </ActionsRow>
        </>
      )}
    </Card>
  );
};

export default GroupsTab;
